/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState) => ({
    isAuthenticated:
        (state.context.session.isAuthenticated || state.context.session.rememberMe) && !state.context.session.isGuest,
    locationPageUrl: getPageLinkByPageType(state, "LocationFinderPage")?.url,
    checkedPartNumbers: state.xProductBomAction.checkedPartNumbers,
    totalQuantity: state.xProductBomAction.totalQuantity,
    checkedProducts: state.xProductBomAction.checkedProducts,
});

export interface FindYourDealerButtonStylesStyles {
    button?: ButtonPresentationProps;
    unauthenticatedMessage?: TypographyProps;
}

export const findYourDealerButtonStyles: FindYourDealerButtonStylesStyles = {
    button: {
        typographyProps: {
            css: css`
                &&& {
                    text-transform: none;
                    font-weight: 500;
                }
            `,
        },
        css: css`
            width: 100%;
            justify-content: center;

            &&& svg {
                stroke-width: 3px;
            }
        `,
    },
    unauthenticatedMessage: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            display: block;
            margin-bottom: 12px;
            @media print {
                display: none;
            }
        `,
    },
};

const styles = findYourDealerButtonStyles;

const ContactUsForRentLink = ({
    product,
    fields: { buttonText, displayMessage, isMultipleProducts },
    isAuthenticated,
    history,
    locationPageUrl,
    checkedPartNumbers,
    totalQuantity,
    checkedProducts,
}: Props) => {
    const redirectToDealersPage = () => {
        // first we need to save product numbers in localstorage
        let erps = "";
        if (isMultipleProducts) {
            // store the partnumbers separated by comma, with quantity as (erpNumber:quantity)
            Object.values(checkedProducts).forEach(product => {
                erps += `${(product as any).erpNumber}:${product.qtyOrdered},`;
            });
            erps += `Total:${Object.keys(checkedPartNumbers)?.length}`;
        }

        if (isMultipleProducts && erps) {
            localStorage && localStorage.setItem("productPartNumber", erps);
        }

        if (locationPageUrl && localStorage) {
            localStorage.setItem("dealerCategory", "Authorized Dealer");
            history.push(`${locationPageUrl}`);
        }
    };

    if (isAuthenticated) {
        return null;
    }

    if (product.cantBuy && !isMultipleProducts) {
        return (
            <Typography {...styles.unauthenticatedMessage}>{siteMessage("Custom_PDP_CantBuyDealerMessage")}</Typography>
        );
    }

    return (
        <StyledWrapper
            css={css`
                @media print {
                    display: none;
                }
            `}
        >
            {displayMessage && (
                <Typography {...styles.unauthenticatedMessage}>{siteMessage("Custom_PDP_FindYourDealer")}</Typography>
            )}
            <Button disabled={isMultipleProducts && !totalQuantity} onClick={redirectToDealersPage} {...styles.button}>
                {buttonText}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(withHistory(ContactUsForRentLink))),
    definition: {
        displayName: "Find Your Dealer Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "buttonText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Find Your Dealer",
                fieldType: "General",
            },
            {
                name: "displayMessage",
                displayName: "Message",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: item => false,
            },
            {
                name: "isMultipleProducts",
                displayName: "multiple products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => false,
            },
        ],
    },
};

export default widgetModule;
